import { appendBraindateSso, safeBraindateTarget } from '#shared/utilities/braindate/link';

// Shared SSO gate for Braindate links. Logged-in visitors go straight to
// Braindate with the SSO pair appended; logged-out visitors are routed to the
// login modal carrying the destination, so `/braindate` can complete the
// hand-off once they're signed in. Shared by braindate-card.vue (one url per
// card, `to` carried through the login round trip) and the Explore Braindates
// button in braindates.vue (no specific destination, so `carryTo: false`
// keeps today's root-redirect behaviour after login).
//
// `url` may be a plain string or a getter (e.g. `() => props.braindate.url`)
// so callers don't need to pre-wrap it in a ref.
export const useBraindateLink = (url, { carryTo = true } = {}) => {
    const { loggedIn, user } = useUserSession();
    const route = useRoute();
    const { public: { braindateUrl } } = useRuntimeConfig();

    const resolve = () => (typeof url === 'function' ? url() : url);

    const href = computed(() => {
        const target = resolve();
        if (!(loggedIn.value && user.value) || !target) return target;

        // Builder entries are unreviewed client content — a typo or a pasted
        // foreign URL must not carry the visitor's email (ext-token) to it.
        // safeBraindateTarget falls back to the configured Braindate origin
        // when the authored url doesn't validate; in that case land on the
        // safe fallback WITHOUT the SSO pair, since the entry itself is the
        // thing that's wrong, not the visitor's destination.
        const safeUrl = safeBraindateTarget(target, braindateUrl);
        if (safeUrl === braindateUrl && target !== braindateUrl) return safeUrl;
        return appendBraindateSso(safeUrl, user.value);
    });

    // New tab only once the visitor is logged in — the only case where this
    // anchor actually leaves for Braindate. Logged out, the click below is
    // cancelled into the login modal, which has to open in THIS tab, so the
    // attribute is omitted rather than left inert: it would otherwise send a
    // modifier-click straight to the raw Braindate url, skipping the gate, and
    // it would promise a screen reader a new tab that never arrives.
    // Same both-halves gate as `href`, so a stale session (user present,
    // loggedIn false) is treated as logged out here too.
    // Named for what it is rather than `target`, which is already the local
    // name for the resolved destination inside href() and handleClick().
    const newTabTarget = computed(() => (loggedIn.value && user.value ? '_blank' : null));

    const handleClick = (e) => {
        if (loggedIn.value && user.value) return;
        e.preventDefault();
        const target = resolve();
        navigateTo({
            path: route.path,
            query: {
                ...route.query,
                modal: '/login',
                action: 'braindate',
                ...(carryTo && target ? { to: target } : {}),
            },
        });
    };

    return { href, target: newTabTarget, handleClick };
};
